import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import seoPages from '../config/seo-pages.json'
import { DEFAULT_SOCIAL_IMAGE, SITE_NAME, absoluteSiteUrl } from '../config/site'
import { getRouteStructuredData } from '../utils/structuredData'

type SeoPage = {
  title: string
  description: string
}

type SEOProps = SeoPage & {
  canonical: string
  image?: string
  noindex?: boolean
  structuredData?: object[]
}

const notFoundSeo: SeoPage = {
  title: 'Page Not Found | Diamond Salon & Spa',
  description: 'The requested Diamond Salon & Spa page could not be found. Return home, meet the team, explore services, book an appointment, or contact the salon.',
}

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    document.head.append(element)
  }
  Object.entries(attributes).forEach(([name, value]) => element!.setAttribute(name, value))
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLLinkElement>(selector)
  if (!element) {
    element = document.createElement('link')
    document.head.append(element)
  }
  Object.entries(attributes).forEach(([name, value]) => element!.setAttribute(name, value))
}

export function SEO({ title, description, canonical, image = DEFAULT_SOCIAL_IMAGE, noindex = false, structuredData = [] }: SEOProps) {
  useEffect(() => {
    const canonicalUrl = absoluteSiteUrl(canonical)
    const imageUrl = absoluteSiteUrl(image)

    document.title = title
    upsertMeta('meta[name="description"]', { name: 'description', content: description })
    upsertMeta('meta[name="robots"]', { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large' })
    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl })
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME })
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title })
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description })
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl })
    upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: 'Diamond Salon & Spa interior in San Juan Capistrano' })
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description })
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl })
    upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: 'Diamond Salon & Spa interior in San Juan Capistrano' })

    const verification = [
      ['google-site-verification', import.meta.env.VITE_GOOGLE_SITE_VERIFICATION],
      ['msvalidate.01', import.meta.env.VITE_BING_SITE_VERIFICATION],
    ] as const
    verification.forEach(([name, content]) => {
      if (content?.trim()) upsertMeta(`meta[name="${name}"]`, { name, content: content.trim() })
    })

    let script = document.head.querySelector<HTMLScriptElement>('script[data-site-structured-data]')
    if (!script) {
      script = document.createElement('script')
      script.type = 'application/ld+json'
      script.dataset.siteStructuredData = 'true'
      document.head.append(script)
    }
    script.textContent = JSON.stringify(structuredData)
  }, [canonical, description, image, noindex, structuredData, title])

  return null
}

export function RouteSEO() {
  const { pathname } = useLocation()
  const normalizedPath = pathname !== '/' ? pathname.replace(/\/+$/, '') : '/'
  const routePath = normalizedPath === '/index.html' ? '/' : normalizedPath
  const page = (seoPages as Record<string, SeoPage>)[routePath]
  const canonical = page ? routePath : '/404'

  return (
    <SEO
      title={page?.title ?? notFoundSeo.title}
      description={page?.description ?? notFoundSeo.description}
      canonical={canonical}
      noindex={!page}
      structuredData={getRouteStructuredData(page ? routePath : '/404')}
    />
  )
}
